import React from "react";
import { Card, CardContent, Typography, Stack } from "@mui/material";

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Box,
  Button
} from "@mui/material";
import { toast } from "react-toastify";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import api from "../../../api";
import ConfirmationDialog from "../../../components/ConfirmationDialog";

const OffenseItem = ({
  offense,
  offenseId,
  searchType,
  verification,
  onVerificationUpdate
}) => {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const handleRemoveClick = () => {
    if (!offense?.internalId) {
      toast.error("Cannot remove: offense ID is missing.");
      return;
    }
    setIsDialogOpen(true);
  };
  const handleConfirm = () => {
    api
      .removeCriminalReport({
        verification_id: verification.id,
        criminal_type: searchType,
        offense_id: offenseId
      })
      .then((resp) => {
        toast.success("Offense removed successfully.");
        onVerificationUpdate(resp.data);
      })
      .catch(() => {
        toast.error("An unexpected error occurred.");
      })
      .finally(() => {
        setIsDialogOpen(false);
      });
  };

  const handleCancel = () => {
    setIsDialogOpen(false);
  };

  return (
    <Box p={1} mb={1} border={1} borderColor="grey.300" borderRadius={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="subtitle2">
          {offense.offenseDescription || "No description"}
        </Typography>
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={handleRemoveClick}
        >
          Remove
        </Button>
      </Box>

      <Stack spacing={0.5}>
        <Typography variant="body2">
          <strong>Statute:</strong> {offense.statute || "—"}
        </Typography>
        <Typography variant="body2">
          <strong>Disposition:</strong> {offense.disposition || "—"}
        </Typography>
        <Typography variant="body2">
          <strong>Offense Date:</strong> {offense.offenseDate || "—"}
        </Typography>
        <Typography variant="body2">
          <strong>Sentence:</strong> {offense.sentence || "—"}
        </Typography>
        <Typography variant="body2">
          <strong>Severity:</strong>{" "}
          {offense.severityClass || offense.severity || "—"}
        </Typography>
        <Typography variant="body2">
          <strong>Agency:</strong> {offense.arrestAgency || "—"}
        </Typography>
        <Typography variant="body2">
          <strong>Jurisdiction:</strong> {offense.countyOrJurisdiction || "—"}
        </Typography>
      </Stack>
      <ConfirmationDialog
        title="Delete Offense"
        open={isDialogOpen}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        cancelProps={{ children: "Cancel" }}
        confirmProps={{ children: "Delete" }}
      >
        Are you sure you want to remove this criminal report? This action cannot
        be undone.
      </ConfirmationDialog>
    </Box>
  );
};
const countOffenses = (subjects = []) => {
  return subjects.reduce((total, subject) => {
    const caseCounts =
      subject.cases?.reduce((caseSum, c) => {
        return caseSum + (c.offenses?.length || 0);
      }, 0) || 0;
    return total + caseCounts;
  }, 0);
};

const CaseSummary = ({
  searchType,
  caseData,
  verification,
  onVerificationUpdate
}) => (
  <Box p={1} mb={2} border={1} borderColor="grey.400" borderRadius={2}>
    <Typography variant="subtitle2" mb={1}>
      Case #{caseData.caseNumber} – {caseData.courtName}
    </Typography>
    {(caseData.offenses || []).map((offense, i) => (
      <OffenseItem
        key={offense.internalId || i}
        offense={offense}
        offenseId={offense?.internalId}
        searchType={searchType}
        verification={verification}
        onVerificationUpdate={onVerificationUpdate}
      />
    ))}
  </Box>
);

export const ResultsAccordion = ({
  results,
  verification,
  onVerificationUpdate
}) => {
  return (
    <Stack spacing={2}>
      {results.map((res, idx) => {
        const count = countOffenses(res.subjects);
        const label = `${res.searchType.toUpperCase()} – ${count} offense${
          count !== 1 ? "s" : ""
        }`;

        return (
          <Accordion key={idx} defaultExpanded={count > 0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    bgcolor: count > 0 ? "error.main" : "success.main",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                    fontWeight: 600
                  }}
                >
                  {count}
                </Box>
                <Typography variant="subtitle1">
                  {res.searchType.toUpperCase()}
                </Typography>
              </Stack>{" "}
            </AccordionSummary>
            <AccordionDetails>
              {res.notFound ? (
                <Typography color="text.secondary">No data found.</Typography>
              ) : (
                res.subjects.map((subject, sIdx) => (
                  <Box key={sIdx} mb={2}>
                    <Typography fontWeight="bold" gutterBottom>
                      {subject.names?.[0]?.firstName}{" "}
                      {subject.names?.[0]?.lastName}
                    </Typography>
                    {subject.cases?.map((caseData, cIdx) => (
                      <CaseSummary
                        key={cIdx}
                        caseData={caseData}
                        searchType={res.searchType}
                        verification={verification}
                        onVerificationUpdate={onVerificationUpdate}
                      />
                    ))}
                    {subject.disclaimers?.length > 0 && (
                      <Box mt={1}>
                        <Typography variant="caption" color="text.secondary">
                          {subject.disclaimers.join(", ")}
                        </Typography>
                      </Box>
                    )}
                    <Divider sx={{ mt: 2 }} />
                  </Box>
                ))
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
};

export const SSNValidationCard = ({ ssnValidation }) => {
  if (!ssnValidation) return null;

  return (
    <Card variant="outlined" sx={{ maxWidth: 400 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          SSN Validation
        </Typography>
        <Stack spacing={1}>
          <Typography>
            <strong>Issuance:</strong> {ssnValidation.issuance}
          </Typography>
          <Typography>
            <strong>Year Issued:</strong> {ssnValidation.yearIssued}
          </Typography>
          <Typography>
            <strong>Validation Status:</strong> {ssnValidation.validationStatus}
          </Typography>
          <Typography>
            <strong>SSN:</strong> {ssnValidation.socialSecurityNumber}
          </Typography>
          {ssnValidation.message && (
            <Typography>
              <strong>Message:</strong> {ssnValidation.message}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const CICOffenses = ({ offenses }) => {
  return (
    <div>
      {offenses.offense ? (
        <Box>
          <Typography variant="body1">
            <strong>Offense Description:</strong> {offenses.offense.description}
          </Typography>
          <Typography variant="body1">
            <strong>Offense Code:</strong> {offenses.offense.offenseCode}
          </Typography>
          <Typography variant="body1">
            <strong>Arrest Date:</strong> {offenses.offense.arrestDate}
          </Typography>
          <Typography variant="body1">
            <strong>Disposition Date:</strong>{" "}
            {offenses.offense.dispositionDate}
          </Typography>
          <Typography variant="body1">
            <strong>Disposition:</strong> {offenses.offense.disposition}
          </Typography>
          {/* Add more fields as necessary */}
        </Box>
      ) : (
        <Typography variant="body2">No offense details available.</Typography>
      )}
    </div>
  );
};

const CICCard = ({ candidate }) => {
  return (
    <Accordion>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        id={`candidate-${candidate.subject.caseNumber}`}
      >
        <Typography variant="h6">
          {candidate.subject.fullName} ({candidate.subject.category})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box mb={2}>
          <Typography variant="body1">
            <strong>Full Name:</strong> {candidate.subject.fullName}
          </Typography>
          <Typography variant="body1">
            <strong>Category:</strong> {candidate.subject.category}
          </Typography>
          <Typography variant="body1">
            <strong>Date of Birth:</strong> {candidate.subject.dOB}
          </Typography>
          <Typography variant="body1">
            <strong>Sex:</strong> {candidate.subject.sex}
          </Typography>
          <Typography variant="body1">
            <strong>Race:</strong> {candidate.subject.race}
          </Typography>
          <Typography variant="body1">
            <strong>State:</strong> {candidate.subject.state}
          </Typography>
          <Typography variant="body1">
            <strong>Height:</strong> {candidate.subject.height}
          </Typography>
          <Typography variant="body1">
            <strong>Weight:</strong> {candidate.subject.weight}
          </Typography>
          <Typography variant="body1">
            <strong>Eye Color:</strong> {candidate.subject.eyeColor}
          </Typography>
          <Typography variant="body1">
            <strong>Hair Color:</strong> {candidate.subject.hairColor}
          </Typography>
        </Box>

        <CICOffenses offenses={candidate.offenses} />
      </AccordionDetails>
    </Accordion>
  );
};

const CICProviderView = ({ data }) => {
  const { candidates } = data;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        CICs List - {candidates.count} Found
      </Typography>

      {candidates.candidate?.map((candidate, idx) => (
        <CICCard key={idx} candidate={candidate} />
      ))}
    </Box>
  );
};

export const CriminalReport = ({ verification: initialVerification }) => {
  const [verification, setVerification] = React.useState(initialVerification);

  const handleVerificationUpdate = (updatedVerification) => {
    setVerification(updatedVerification);
  };

  return (
    <div>
      {/* If vendor === cleara */}
      {verification.data.vendor === "cleara" ||
      verification.data.rawResponse?.ssnValidation ? (
        <>
          <SSNValidationCard
            ssnValidation={verification.data.rawResponse?.ssnValidation}
          />
          <br />
          <hr />
          <br />
          <ResultsAccordion
            results={verification.data.rawResponse?.results}
            verification={verification}
            onVerificationUpdate={handleVerificationUpdate}
          />
        </>
      ) : verification.data.vendor === "cic" ||
        verification.data.rawResponse?.xMLCRIM ? (
        <CICProviderView
          data={verification.data.rawResponse?.xMLCRIM.cICcriminal}
        />
      ) : (
        <div>
          <Typography variant="h6">
            No data available. Please check JSON
          </Typography>
        </div>
      )}
    </div>
  );
};
